import styled from "styled-components";
import {useNavigate} from "react-router-dom";
import IllUpdate from "../../assets/images/Update.png";
import TimedButton from "../atoms/TimedButton/TimedButton";

export type NiveauErreur = "bloc" | "page" | "application";

interface SomethingWrongCtrlProps{
    className?:string;
    error?:Error|null;
    /** Remonte la frontiere sans recharger la page. */
    retry?:()=>void;
    /** Ce qui a echoue : une tuile, un ecran, ou l'application entiere. */
    niveau?:NiveauErreur;
    /** Le nom du bloc, quand on en a un a citer. */
    nom?:string;
}

/**
 * Une nouvelle version est disponible, ou quelque chose a casse ?
 *
 * L'ecran affirmait « L'application a été mise à jour » quelle que soit la
 * cause. C'est vrai dans un seul cas — un fichier demande qu'un deploiement
 * vient de remplacer, que le navigateur signale par un `ChunkLoadError` — et
 * faux dans tous les autres, ou l'on demandait alors a l'utilisateur de
 * rafraichir pour un bug que le rafraichissement reproduit a l'identique.
 *
 * D'ou la boucle : recharger la meme route qui plante ramene au meme ecran.
 */
function estUneMiseAJour(error?:Error|null):boolean{
    if(!error){
        return false;
    }
    const nom = error.name || "";
    const message = error.message || "";
    return nom === "ChunkLoadError"
        || /Loading chunk [\d]+ failed/i.test(message)
        || /dynamically imported module/i.test(message);
}

/**
 * Un code court a citer au support.
 *
 * Sans lui, « j'ai eu une erreur » ne mene nulle part. Il est calcule sur le
 * nom et le message, donc deux personnes qui rencontrent la meme chose citent
 * le meme code.
 */
export function codeIncident(error?:Error|null):string{
    const source = `${error?.name||"?"}|${error?.message||"?"}`;
    let hash = 0;
    for(let i=0; i<source.length; i++){
        hash = ((hash << 5) - hash) + source.charCodeAt(i);
        hash = hash & hash;
    }
    return Math.abs(hash).toString(36).toUpperCase().slice(0,6);
}

/**
 * Le niveau bloc.
 *
 * Une tuile qui tombe au milieu de vingt-quatre qui vont bien. L'utilisateur
 * regardait autre chose : on tient dans la place perdue, on ne propose ni de
 * naviguer ni de recharger — ce serait interrompre toute la page pour une tuile.
 */
const BlocCtrl = ({className, error, retry}:SomethingWrongCtrlProps)=>(
    <div className={`something_wrong_bloc ${className}`}>
        <div className={`texte`}>
            <p className={`titre`}>Ce contenu n'a pas pu s'afficher</p>
            <p className={`code`}>code {codeIncident(error)}</p>
        </div>
        {retry &&
            <button className={"lien_secondaire"} onClick={retry} type={"button"}>Réessayer</button>
        }
    </div>
);

/**
 * Les niveaux page et application.
 *
 * La difference n'est pas cosmetique : au niveau page la navigation est rendue
 * au-dessus de la frontiere, donc elle survit et « retour au tableau de bord »
 * fonctionne. Au niveau application elle est tombee avec le reste, et ce meme
 * bouton serait un mensonge. On n'y propose que le rechargement.
 */
const PleinCtrl = ({className, error, retry, niveau}:SomethingWrongCtrlProps)=>{
    const navigate = useNavigate();
    const miseAJour = estUneMiseAJour(error);
    const application = niveau === "application";

    return (
        <div className={`something_wrong ${application ? "plein_ecran" : ""} ${className}`}>
            <div className={`in_refresh`}>
                <img src={IllUpdate} alt={miseAJour ? "mise à jour" : "incident"}/>
                <div className={`legende`}>
                    {miseAJour ?
                        <>
                            <p className={`leg1`}>Une nouvelle version de l'application est disponible</p>
                            <p className={`leg2`}>Rechargez la page pour en profiter. Vos données ne sont pas perdues.</p>
                        </> : application ?
                        <>
                            <p className={`leg1`}>L'application n'a pas pu démarrer</p>
                            <p className={`leg2`}>
                                Rechargez la page. Si le problème revient, indiquez ce code
                                au support : <strong>{codeIncident(error)}</strong>
                            </p>
                        </> :
                        <>
                            <p className={`leg1`}>Cette page n'a pas pu s'afficher</p>
                            <p className={`leg2`}>
                                Le reste de l'application fonctionne : vous pouvez continuer ailleurs.
                                Si le problème revient, indiquez ce code au support : <strong>{codeIncident(error)}</strong>
                            </p>
                        </>
                    }
                </div>
                {/* L'action mise en avant est celle qui a une chance d'aider.
                    Pour une mise a jour, recharger repare vraiment. Sinon,
                    recharger rejoue le meme plantage : on propose d'abord de
                    partir, et de reessayer sans recharger. */}
                <div className={`actions`}>
                    {miseAJour || application ?
                        <TimedButton themeColor={"Secondary"} onClick={()=>window.location.reload()}>Recharger l'application</TimedButton> :
                        <>
                            <TimedButton themeColor={"Secondary"} onClick={()=>navigate("/timed_application/dashboard")}>Retour au tableau de bord</TimedButton>
                            {retry &&
                                <button className={"lien_secondaire"} onClick={retry} type={"button"}>Réessayer cette page</button>
                            }
                            <button className={"lien_secondaire"} onClick={()=>window.location.reload()} type={"button"}>Recharger l'application</button>
                        </>
                    }
                </div>
            </div>
        </div>
    )
}

const SomethingWrongCtrl = (props:SomethingWrongCtrlProps)=>(
    props.niveau === "bloc" ? <BlocCtrl {...props}/> : <PleinCtrl {...props}/>
);

export const SomethingWrong = styled(SomethingWrongCtrl)`
  /* Niveau bloc : on remplit la place perdue, sans jamais la depasser. Une tuile
     de tableau de bord a une hauteur imposee par la grille. */
  &.something_wrong_bloc{
    width: 100%;
    height: 100%;
    min-height: 60px;
    box-sizing: border-box;
    padding: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    overflow: hidden;
    background: white;
    border-radius: 4px;
    border-left: solid ${props => props.theme.NeutreLight} 4px;
    .texte{
      text-align: center;
      overflow: hidden;
      .titre{
        font-size: 14px;
        font-weight: bold;
        color: ${props => props.theme.NeutreDark};
      }
      .code{
        font-size: 12px;
        color: ${props => props.theme.NeutreLight};
      }
    }
  }

  /* Niveau page : plus de position fixed couvrant toute la fenetre. Les
     frontieres de route sont posees sous le layout, donc la navigation est
     rendue au-dessus. La recouvrir d'un blanc opaque etait ce qui enfermait
     l'utilisateur. */
  &.something_wrong{
    width: 100%;
    min-height: 60vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
  }

  /* Niveau application : la navigation n'existe plus, il n'y a rien a laisser
     visible. Couvrir l'ecran est ici la description exacte de ce qui a echoue. */
  &.plein_ecran{
    position: fixed;
    inset: 0;
    z-index: 9999;
    min-height: 100vh;
  }

  .in_refresh{
    text-align: center;
    max-width: 520px;
    padding: 20px;
    img{
      max-width: 160px;
    }
    .legende{
      margin: 30px auto;
      .leg1{
        font-size: 18px;
        font-weight: bold;
        color: ${props => props.theme.PrimaryDark};
      }
      .leg2{
        margin-top: 10px;
        font-size: 14px;
        color: ${props => props.theme.NeutreDark};
      }
    }
    .actions{
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
    }
  }

  .lien_secondaire{
    border: none;
    background: none;
    padding: 0;
    font-size: 13px;
    color: ${props => props.theme.Primary};
    text-decoration: underline;
    cursor: pointer;
  }
`
